import React from 'react';
import { Flex, Text, TextField, TextArea, Separator, Button, Tooltip } from "@radix-ui/themes";
import { InstagramLogoIcon, LinkedInLogoIcon, UploadIcon } from "@radix-ui/react-icons";


// Template metadata
export const templateMeta = {
  id: 'elegant-classic',
  name: 'Elegant Classic',
  description: 'Sophisticated and timeless design with serif fonts',
  category: 'welcome',
  defaultPurpose: 'welcome',
};

// Default form values for this template
export const getDefaultValues = (purpose = 'welcome') => {
  const purposeDefaults = {
    welcome: {
      headline: 'Welcome to Maison Elite',
      subheadline: 'A refined experience awaits',
      body: "It's our pleasure to welcome you to our distinguished community. We believe in delivering excellence in everything we do, and we're honored you've chosen to join us.",
      ctaText: 'Begin Your Journey',
      ctaUrl: '',
      testimonial: 'An exceptional experience from start to finish. Truly world-class service.',
    },
    review: {
      headline: "We'd love your feedback!",
      subheadline: 'Your opinion matters to us',
      body: 'Thank you for visiting us. Your opinion matters and helps us improve. Would you take a moment to share your experience?',
      ctaText: 'Leave a Review',
      ctaUrl: '{{review_url}}',
      testimonial: '',
    },
    promotion: {
      headline: 'Exclusive Offer Just for You',
      subheadline: 'Limited time opportunity',
      body: 'As a valued guest, we wanted to share this special offer with you.',
      ctaText: 'Claim Offer',
      ctaUrl: '',
      testimonial: '',
    },
  };

  const defaults = purposeDefaults[purpose] || purposeDefaults.welcome;

  return {
    // Content
    headline: defaults.headline,
    subheadline: defaults.subheadline,
    body: defaults.body,
    ctaText: defaults.ctaText,
    ctaUrl: defaults.ctaUrl,
    testimonial: defaults.testimonial,
    // Brand
    companyName: 'Maison Élite',
    logoUrl: '',
    // Colors
    primaryColor: '#2C3E50',
    accentColor: '#C9A961',
    // Social
    twitterUrl: 'https://twitter.com',
    facebookUrl: 'https://facebook.com',
    instagramUrl: 'https://instagram.com',
    linkedinUrl: 'https://linkedin.com',
    tiktokUrl: 'https://tiktok.com',
    pinterestUrl: 'https://pinterest.com',
  };
};

// Form fields configuration for Content tab
export const ContentFields = ({ formData, updateField }) => (
  <Flex direction="column" gap="4">
    
    {/* Headline input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Headline</Text>
      <TextField.Root
        placeholder="Thanks for visiting us!"
        value={formData.headline}
        onChange={(e) => updateField('headline', e.target.value)}
        style={{ alignSelf: 'stretch' }}
      />
    </Flex>
    
    {/* Subheadline input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Subheadline</Text>
      <TextField.Root 
        placeholder="A refined experience awaits you"
        value={formData.subheadline}
        onChange={(e) => updateField('subheadline', e.target.value)}
        style={{ alignSelf: 'stretch' }}
      />
    </Flex>
    
    {/* Body text input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Body text</Text>
      <TextArea 
        placeholder="It's our pleasure to welcome you to our distinguished community..." 
        value={formData.body}
        onChange={(e) => updateField('body', e.target.value)}
        rows={5}
        style={{ width: '100%' }}
      />
    </Flex>
    
    {/* Button text input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Button text</Text>
      <TextField.Root 
        placeholder="Begin Your Journey" 
        value={formData.ctaText}
        onChange={(e) => updateField('ctaText', e.target.value)}
        style={{ alignSelf: 'stretch' }}
      />
    </Flex>

    {/* Button URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Button URL</Text>
      <TextField.Root 
        placeholder="https://your-website.com" 
        value={formData.ctaUrl}
        onChange={(e) => updateField('ctaUrl', e.target.value)}
        style={{ alignSelf: 'stretch' }}
      />
      <Text size="1" color="gray">Link to your website or a specific page.</Text>
    </Flex>

    <Flex px="4" pb="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Testimonial quote (optional)</Text>
      <TextArea 
        placeholder="An exceptional experience..." 
        value={formData.testimonial}
        onChange={(e) => updateField('testimonial', e.target.value)}
        rows={2}
        style={{ width: '100%' }}
      />
    </Flex>
  </Flex>
);

// Form fields configuration for Brand tab
export const BrandFields = ({ 
  formData, 
  updateField,
  logoPreview,
  logoFileName,
  logoInputRef,
  handleLogoUpload,
  handleLogoClear,
  triggerLogoInput,
  backgroundPreview,
  backgroundFileName,
  backgroundInputRef,
  handleBackgroundImageUpload,
  handleBackgroundImageClear,
  triggerBackgroundInput,
}) => (
  <Flex direction="column" gap="4" style={{ alignSelf: "stretch" }}>

    <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="1" weight="regular" color="gray">
        Customize your brand details to make the email truly yours.
      </Text>
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
    
    {/* Company Information */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Company name</Text>
      <TextField.Root 
        placeholder="Maison Élite" 
        value={formData.companyName}
        onChange={(e) => updateField('companyName', e.target.value)}
        style={{ width: '100%' }}
      />
    </Flex>
    
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
    
    {/* Logo Upload */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Logo</Text>
      <Flex gap="2" style={{ width: '100%' }}>
        <input 
          type="file" 
          style={{ display: "none" }} 
          ref={logoInputRef} 
          onChange={handleLogoUpload} 
          accept="image/*" 
        />
        <Button
          type="button"
          size="2"
          variant="surface"
          color="gray"
          style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
          onClick={triggerLogoInput}
        >
          {logoPreview ? (
            <Tooltip content="Upload new to replace">
              <Text>{logoFileName?.length >= 18 ? `${logoFileName.slice(0, 18)}...` : logoFileName}</Text>
            </Tooltip>
          ) : (
            <>
              <UploadIcon />
              <Text>Upload</Text>
            </>
          )}
        </Button>
        {logoPreview && (
          <Button type="button" size="2" variant="surface" color="gray" onClick={handleLogoClear}>
            Clear
          </Button>
        )}
      </Flex>
      <Text size="1" color="gray" mt="1">Recommended dimensions: 200 x 96 px. Maximum file size: 1 MB.</Text>
    </Flex>
    
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
    
    {/* Background Image Upload */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Background image</Text>
      <Flex gap="2" style={{ width: '100%' }}>
        <input 
          type="file" 
          style={{ display: "none" }} 
          ref={backgroundInputRef} 
          onChange={handleBackgroundImageUpload} 
          accept="image/*" 
        />
        <Button
          type="button"
          size="2"
          variant="surface"
          color="gray"
          style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
          onClick={triggerBackgroundInput}
        >
          {backgroundPreview ? (
            <Tooltip content="Upload new to replace">
              <Text>{backgroundFileName?.length >= 18 ? `${backgroundFileName.slice(0, 18)}...` : backgroundFileName}</Text>
            </Tooltip>
          ) : (
            <>
              <UploadIcon />
              <Text>Upload</Text>
            </>
          )}
        </Button>
        {backgroundPreview && (
          <Button type="button" size="2" variant="surface" color="gray" onClick={handleBackgroundImageClear}>
            Clear
          </Button>
        )}
      </Flex>
      <Text size="1" color="gray" mt="1">Recommended dimensions: 600 x 400 px. Maximum file size: 1 MB.</Text>
    </Flex>
    
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
  </Flex>
);

// Form fields configuration for Colors tab
export const ColorsFields = ({ formData, updateField }) => (
  <Flex direction="column" gap="4">

    <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="1" weight="regular" color="gray">
        Choose colours that reflect your brand's elegance, style, and sophistication.
      </Text>
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>

    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Primary colour</Text>
      <Flex direction="column" style={{ alignSelf: "stretch" }}>
        <label htmlFor="primary_colour_picker" style={{ position: "relative", display: "inline-block", width: "100%" }}>
          <input
            type="color"
            id="primary_colour_picker"
            value={formData.primaryColor}
            onChange={(e) => updateField('primaryColor', e.target.value)}
            style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0, cursor: "pointer" }}
          />
          <div style={{ width: "100%", height: "32px", backgroundColor: formData.primaryColor, borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)", cursor: "pointer" }}></div>
        </label>
      </Flex>
    </Flex>
    
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Accent colour</Text>
      <Flex direction="column" style={{ alignSelf: "stretch" }}>
        <label htmlFor="accent_colour_picker" style={{ position: "relative", display: "inline-block", width: "100%" }}>
          <input
            type="color"
            id="accent_colour_picker"
            value={formData.accentColor}
            onChange={(e) => updateField('accentColor', e.target.value)}
            style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0, cursor: "pointer" }}
          />
          <div style={{ width: "100%", height: "32px", backgroundColor: formData.accentColor, borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)", cursor: "pointer" }}></div>
        </label>
      </Flex>
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
  </Flex>
);

// Form fields configuration for Social tab
export const SocialFields = ({ formData, updateField }) => (
  <Flex direction="column" gap="4">
    {/* Help text for Social tab */}
    <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="1" weight="regular" color="gray">
        Add your social media profile URLs to include social icons in the email footer.
      </Text>
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
    {/* Twitter URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <svg viewBox="0 0 1200 1227" width="12" height="12" fill="var(--gray-12)" xmlns="http://www.w3.org/2000/svg">
          <path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z" fill="var(--gray-12)"/>
        </svg>
        <Text size="2" weight="medium">X URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://twitter.com" 
        value={formData.twitterUrl}
        onChange={(e) => updateField('twitterUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    
    {/* Facebook URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 750 850" width="14" height="14" fill="var(--gray-12)" style={{ opacity: 1 }}>
          <path  d="M659 8q34 0 59 24t24 58v577q0 35-24 59t-59 24H494V461h83l23-102H494v-69q0-22 13-41t44-19h49V123h-74q-79 0-117 42t-38 110v84h-83v102h83v289H82q-34 0-58-24T0 667V90q0-34 24-58T82 8z"/>
        </svg>
        <Text size="2" weight="medium">Facebook URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://facebook.com" 
        value={formData.facebookUrl}
        onChange={(e) => updateField('facebookUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    
    {/* Instagram URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <InstagramLogoIcon />
        <Text size="2" weight="medium">Instagram URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://instagram.com" 
        value={formData.instagramUrl}
        onChange={(e) => updateField('instagramUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    
    {/* LinkedIn URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <LinkedInLogoIcon />
        <Text size="2" weight="medium">LinkedIn URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://linkedin.com" 
        value={formData.linkedinUrl}
        onChange={(e) => updateField('linkedinUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    
    {/* TikTok URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <svg viewBox="0 0 2859 3333" width="14" height="14" fill="var(--gray-12)" xmlns="http://www.w3.org/2000/svg">
          <path d="M2081 0c55 473 319 755 778 785v532c-266 26-499-61-770-225v995c0 1264-1378 1659-1932 753-356-583-138-1606 1004-1647v561c-87 14-180 36-265 65-254 86-398 247-358 531 77 544 1075 705 992-358V1h551z"/>
        </svg>
        <Text size="2" weight="medium">TikTok URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://tiktok.com" 
        value={formData.tiktokUrl}
        onChange={(e) => updateField('tiktokUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    
    {/* Pinterest URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <svg viewBox="0 0 496 512" width="14" height="14" fill="var(--gray-12)" xmlns="http://www.w3.org/2000/svg">
          <path d="M496 256c0 137-111 248-248 248-25.6 0-50.2-3.9-73.4-11.1 10.1-16.5 25.2-43.5 30.8-65 3-11.6 15.4-59 15.4-59 8.1 15.4 31.7 28.5 56.8 28.5 74.8 0 128.7-68.8 128.7-154.3 0-81.9-66.9-143.2-152.9-143.2-107 0-163.9 71.8-163.9 150.1 0 36.4 19.4 81.7 50.3 96.1 4.7 2.2 7.2 1.2 8.3-3.3.8-3.4 5-20.3 6.9-28.1.6-2.5.3-4.7-1.7-7.1-10.1-12.5-18.3-35.3-18.3-56.6 0-54.7 41.4-107.6 112-107.6 60.9 0 103.6 41.5 103.6 100.9 0 67.1-33.9 113.6-78 113.6-24.3 0-42.6-20.1-36.7-44.8 7-29.5 20.5-61.3 20.5-82.6 0-19-10.2-34.9-31.4-34.9-24.9 0-44.9 25.7-44.9 60.2 0 22 7.4 36.8 7.4 36.8s-24.5 103.8-29 123.2c-5 21.4-3 51.6-.9 71.2C65.4 450.9 0 361.1 0 256 0 119 111 8 248 8s248 111 248 248z"/>
        </svg>
        <Text size="2" weight="medium">Pinterest URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://pinterest.com" 
        value={formData.pinterestUrl}
        onChange={(e) => updateField('pinterestUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    
  </Flex>
);

// Generate MJML for this template
export const generateMjml = (formData) => {
  const { 
    headline, subheadline, body, ctaText, ctaUrl, 
    primaryColor, accentColor, companyName, logoUrl, backgroundImageUrl,
    testimonial,
    twitterUrl, facebookUrl, instagramUrl, linkedinUrl, tiktokUrl, pinterestUrl 
  } = formData;
  
  const ctaSection = ctaText ? `
      <mj-button background-color="transparent" color="${primaryColor}" href="${ctaUrl || '#'}" border="${primaryColor} 2px solid" border-radius="0" font-size="12px" padding="30px 0" letter-spacing="2px" font-weight="600">
        ${ctaText.toUpperCase()}
      </mj-button>` : '';

  const testimonialSection = testimonial ? `
  <mj-section background-color="#f8f8f8" padding="40px 20px">
    <mj-column>
      <mj-text align="center" font-size="16px" font-style="italic" color="#555" line-height="1.8">
        "${testimonial}"
      </mj-text>
      <mj-text align="center" font-size="14px" color="${accentColor}" padding-top="15px">
        — Satisfied Member
      </mj-text>
    </mj-column>
  </mj-section>` : '';

  // Hero section with background image and centered logo
  // Only show header section if there's a background image or logo
  const heroSection = backgroundImageUrl ? `
  <mj-section background-url="${backgroundImageUrl}" background-size="cover" background-repeat="no-repeat" background-position="center center" padding="60px 20px">
    <mj-column>
      ${logoUrl ? `<mj-image src="${logoUrl}" width="120px" align="center" />` : ``}
    </mj-column>
  </mj-section>` : (logoUrl ? `
  <mj-section background-color="${primaryColor}" padding="8px 0"></mj-section>
  <mj-section background-color="#ffffff" padding="40px 20px 20px">
    <mj-column>
      <mj-image src="${logoUrl}" width="60px" align="center" padding-bottom="15px" />
    </mj-column>
  </mj-section>` : '');

  // Welcome section with company name
  const welcomeSection = `
  <mj-section background-color="#ffffff" padding="30px 20px 10px">
    <mj-column>
      <mj-text align="center" font-size="14px" letter-spacing="2px" color="${accentColor}" text-transform="uppercase">
        Welcome to
      </mj-text>
      <mj-text align="center" font-size="28px" letter-spacing="4px" color="${primaryColor}" text-transform="uppercase" font-weight="400" padding-top="5px">
        ${companyName.toUpperCase()}
      </mj-text>
    </mj-column>
  </mj-section>`;

  return `<mjml>
<mj-head>
  <mj-attributes>
    <mj-all font-family="Georgia, 'Times New Roman', serif" />
    <mj-text font-size="16px" line-height="1.8" color="#555555" />
  </mj-attributes>
</mj-head>
<mj-body background-color="#f5f5f0">${heroSection}${welcomeSection}
  <mj-section background-color="#ffffff" padding="20px 20px">
    <mj-column>
      <mj-text align="center" color="${accentColor}" font-size="16px" padding-bottom="10px">
        ——— ◆ ———
      </mj-text>
      <mj-text align="center" font-size="32px" color="${primaryColor}" font-weight="400" padding-bottom="10px">
        ${headline}
      </mj-text>
      <mj-text align="center" font-size="16px" color="${accentColor}" font-style="italic">
        ${subheadline}
      </mj-text>
    </mj-column>
  </mj-section>
  <mj-section background-color="#ffffff" padding="20px 40px 30px">
    <mj-column>
      <mj-text align="center" font-size="16px" line-height="1.8" color="#555555">
        ${body.split('\n').join('<br/>')}
      </mj-text>${ctaSection}
    </mj-column>
  </mj-section>${testimonialSection}
  <mj-section background-color="${primaryColor}" padding="30px 20px">
    <mj-column>
      <mj-social mode="horizontal" align="center" icon-size="24px" icon-padding="8px">
        ${twitterUrl ? `<mj-social-element name="x" href="${twitterUrl}" background-color="transparent" src="https://img.icons8.com/ios-filled/50/ffffff/twitterx--v1.png" alt="X" />` : ''}
        ${facebookUrl ? `<mj-social-element name="facebook" href="${facebookUrl}" background-color="transparent" src="https://img.icons8.com/ios-filled/50/ffffff/facebook-new.png" alt="Facebook" />` : ''}
        ${instagramUrl ? `<mj-social-element name="instagram" href="${instagramUrl}" background-color="transparent" src="https://img.icons8.com/ios-filled/50/ffffff/instagram-new--v1.png" alt="Instagram" />` : ''}
        ${linkedinUrl ? `<mj-social-element name="linkedin" href="${linkedinUrl}" background-color="transparent" src="https://img.icons8.com/ios-filled/50/ffffff/linkedin.png" alt="LinkedIn" />` : ''}
        ${tiktokUrl ? `<mj-social-element name="tiktok" href="${tiktokUrl}" background-color="transparent" src="https://img.icons8.com/ios-filled/50/ffffff/tiktok.png" alt="TikTok" />` : ''}
        ${pinterestUrl ? `<mj-social-element name="pinterest" href="${pinterestUrl}" background-color="transparent" src="https://img.icons8.com/ios-filled/50/ffffff/pinterest.png" alt="Pinterest" />` : ''}
      </mj-social>
      <mj-text align="center" font-size="12px" color="#ffffff" letter-spacing="2px" padding-top="20px">
        © ${new Date().getFullYear()} ${companyName}. All rights reserved.
      </mj-text>
    </mj-column>
  </mj-section>
</mj-body>
</mjml>`;
};

// Generate Preview HTML for this template
export const generatePreviewHtml = (formData) => {
  const { 
    headline, subheadline, body, ctaText, ctaUrl, 
    primaryColor, accentColor, companyName, logoUrl, backgroundImageUrl,
    testimonial,
    twitterUrl, facebookUrl, instagramUrl, linkedinUrl, tiktokUrl, pinterestUrl 
  } = formData;
  
  const ctaButton = ctaText ? `
    <a href="${ctaUrl || '#'}" style="display: inline-block; background-color: transparent; color: ${primaryColor}; padding: 14px 32px; text-decoration: none; border: 2px solid ${primaryColor}; font-weight: 600; margin-top: 30px; font-size: 12px; letter-spacing: 2px; text-transform: uppercase; font-family: Georgia, 'Times New Roman', serif;">
      ${ctaText.toUpperCase()}
    </a>` : '';

  const testimonialSection = testimonial ? `
    <div style="background-color: #f8f8f8; padding: 40px; text-align: center; margin-top: 0;">
      <p style="font-size: 16px; font-style: italic; color: #555; line-height: 1.8; margin: 0;">
        "${testimonial}"
      </p>
      <p style="font-size: 14px; color: ${accentColor}; margin-top: 15px;">— Satisfied Member</p>
    </div>` : '';

  // Hero section with background image and centered logo
  // Only show header section if there's a background image or logo
  const heroSection = backgroundImageUrl ? `
    <!-- Hero Header with Background Image -->
    <div style="background-image: url('${backgroundImageUrl}'); background-size: cover; background-position: center center; background-repeat: no-repeat; padding: 60px 20px; text-align: center;">
      ${logoUrl 
        ? `<img src="${logoUrl}" alt="Logo" style="width: 120px; margin: 0 auto; display: block;" />`
        : ``}
    </div>` : (logoUrl ? `
    <!-- Header Bar -->
    <div style="background-color: ${primaryColor}; height: 8px;"></div>
    
    <!-- Logo -->
    <div style="background-color: #ffffff; padding: 40px 20px 20px; text-align: center;">
      <img src="${logoUrl}" alt="Logo" style="width: 60px; margin: 0 auto; display: block;" />
    </div>` : '');

  // Welcome section with company name
  const welcomeSection = `
    <!-- Welcome Section -->
    <div style="background-color: #ffffff; padding: 30px 20px 10px; text-align: center;">
      <p style="font-size: 14px; letter-spacing: 2px; color: ${accentColor}; text-transform: uppercase; margin: 0;">Welcome to</p>
      <h2 style="font-size: 28px; letter-spacing: 4px; color: ${primaryColor}; text-transform: uppercase; font-weight: 400; margin: 5px 0 0 0;">
        ${companyName.toUpperCase()}
      </h2>
    </div>`;

  return `
    <div style="background-color: #f5f5f0; padding: 0; font-family: Georgia, 'Times New Roman', serif;">
      <div style="max-width: 600px; margin: 0 auto; box-shadow: 0 4px 20px rgba(0,0,0,0.1);">
        ${heroSection}
        ${welcomeSection}
        
        <!-- Divider & Headline -->
        <div style="background-color: #ffffff; padding: 20px 20px; text-align: center;">
          <div style="color: ${accentColor}; font-size: 16px; margin-bottom: 10px;">——— ◆ ———</div>
          <h1 style="font-size: 32px; color: ${primaryColor}; font-weight: 400; margin: 0 0 10px 0;">${headline}</h1>
          <p style="font-size: 16px; color: ${accentColor}; font-style: italic; margin: 0;">${subheadline}</p>
        </div>
        
        <!-- Body Content -->
        <div style="background-color: #ffffff; padding: 20px 40px 30px; text-align: center;">
          <p style="color: #555555; font-size: 16px; line-height: 1.8; margin: 0;">
            ${body.split('\n').join('<br/>')}
          </p>
          <div style="text-align: center;">
            ${ctaButton}
          </div>
        </div>
        
        <!-- Testimonial -->
        ${testimonialSection}
        
        <!-- Footer -->
        <div style="background-color: ${primaryColor}; padding: 30px 20px; text-align: center;">
          <div style="margin-bottom: 20px;">
            ${twitterUrl ? `<a href="${twitterUrl}" style="color: #ffffff; text-decoration: none; margin: 0 8px; display: inline-block; vertical-align: middle;"><svg viewBox="0 0 1200 1227" width="18" height="18" fill="#ffffff" xmlns="http://www.w3.org/2000/svg"><path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z"/></svg></a>` : ''}
            ${facebookUrl ? `<a href="${facebookUrl}" style="color: #ffffff; text-decoration: none; margin: 0 8px; display: inline-block; vertical-align: middle;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 750 850" width="18" height="18" fill="#ffffff"><path d="M659 8q34 0 59 24t24 58v577q0 35-24 59t-59 24H494V461h83l23-102H494v-69q0-22 13-41t44-19h49V123h-74q-79 0-117 42t-38 110v84h-83v102h83v289H82q-34 0-58-24T0 667V90q0-34 24-58T82 8z"/></svg></a>` : ''}
            ${instagramUrl ? `<a href="${instagramUrl}" style="color: #ffffff; text-decoration: none; margin: 0 8px; display: inline-block; vertical-align: middle;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 15 15" fill="none"><path d="M7.5 5C6.11929 5 5 6.11929 5 7.5C5 8.88071 6.11929 10 7.5 10C8.88071 10 10 8.88071 10 7.5C10 6.11929 8.88071 5 7.5 5Z" fill="#ffffff"/><path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 0C2.01472 0 0 2.01472 0 4.5V10.5C0 12.9853 2.01472 15 4.5 15H10.5C12.9853 15 15 12.9853 15 10.5V4.5C15 2.01472 12.9853 0 10.5 0H4.5ZM4 7.5C4 5.567 5.567 4 7.5 4C9.433 4 11 5.567 11 7.5C11 9.433 9.433 11 7.5 11C5.567 11 4 9.433 4 7.5ZM11 4H12V3H11V4Z" fill="#ffffff"/></svg></a>` : ''}
            ${linkedinUrl ? `<a href="${linkedinUrl}" style="color: #ffffff; text-decoration: none; margin: 0 8px; display: inline-block; vertical-align: middle;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 15 15" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M2 1C1.44772 1 1 1.44772 1 2V13C1 13.5523 1.44772 14 2 14H13C13.5523 14 14 13.5523 14 13V2C14 1.44772 13.5523 1 13 1H2ZM3.05 6H4.95V12H3.05V6ZM5.075 4.005C5.075 4.59871 4.59371 5.08 4 5.08C3.4063 5.08 2.925 4.59871 2.925 4.005C2.925 3.41129 3.4063 2.93 4 2.93C4.59371 2.93 5.075 3.41129 5.075 4.005ZM12 8.35713C12 6.55208 10.8334 5.85033 9.67449 5.85033C9.29502 5.83163 8.91721 5.91119 8.57874 6.08107C8.32172 6.21007 8.05265 6.50523 7.84516 7.01348H7.79179V6.00044H6V12.0047H7.90616V8.8112C7.8786 8.48413 7.98327 8.06142 8.19741 7.80987C8.41156 7.55832 8.71789 7.49825 8.95015 7.46774H9.02258C9.62874 7.46774 10.0786 7.84301 10.0786 8.78868V12.0047H11.9847L12 8.35713Z" fill="#ffffff"/></svg></a>` : ''}
            ${tiktokUrl ? `<a href="${tiktokUrl}" style="color: #ffffff; text-decoration: none; margin: 0 8px; display: inline-block; vertical-align: middle;"><svg viewBox="0 0 2859 3333" width="18" height="18" fill="#ffffff" xmlns="http://www.w3.org/2000/svg"><path d="M2081 0c55 473 319 755 778 785v532c-266 26-499-61-770-225v995c0 1264-1378 1659-1932 753-356-583-138-1606 1004-1647v561c-87 14-180 36-265 65-254 86-398 247-358 531 77 544 1075 705 992-358V1h551z"/></svg></a>` : ''}
            ${pinterestUrl ? `<a href="${pinterestUrl}" style="color: #ffffff; text-decoration: none; margin: 0 8px; display: inline-block; vertical-align: middle;"><svg viewBox="0 0 496 512" width="18" height="18" fill="#ffffff" xmlns="http://www.w3.org/2000/svg"><path d="M496 256c0 137-111 248-248 248-25.6 0-50.2-3.9-73.4-11.1 10.1-16.5 25.2-43.5 30.8-65 3-11.6 15.4-59 15.4-59 8.1 15.4 31.7 28.5 56.8 28.5 74.8 0 128.7-68.8 128.7-154.3 0-81.9-66.9-143.2-152.9-143.2-107 0-163.9 71.8-163.9 150.1 0 36.4 19.4 81.7 50.3 96.1 4.7 2.2 7.2 1.2 8.3-3.3.8-3.4 5-20.3 6.9-28.1.6-2.5.3-4.7-1.7-7.1-10.1-12.5-18.3-35.3-18.3-56.6 0-54.7 41.4-107.6 112-107.6 60.9 0 103.6 41.5 103.6 100.9 0 67.1-33.9 113.6-78 113.6-24.3 0-42.6-20.1-36.7-44.8 7-29.5 20.5-61.3 20.5-82.6 0-19-10.2-34.9-31.4-34.9-24.9 0-44.9 25.7-44.9 60.2 0 22 7.4 36.8 7.4 36.8s-24.5 103.8-29 123.2c-5 21.4-3 51.6-.9 71.2C65.4 450.9 0 361.1 0 256 0 119 111 8 248 8s248 111 248 248z"/></svg></a>` : ''}
          </div>
          <p style="font-size: 12px; color: #ffffff; letter-spacing: 2px; margin: 0;">© ${new Date().getFullYear()} ${companyName}. All rights reserved.</p>
        </div>
      </div>
    </div>
  `;
};

// Default export with all template utilities
const ElegantClassicTemplate = {
  meta: templateMeta,
  getDefaultValues,
  ContentFields,
  BrandFields,
  ColorsFields,
  SocialFields,
  generateMjml,
  generatePreviewHtml,
};

export default ElegantClassicTemplate;
